import { useState, type ReactNode } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface ReportSectionProps {
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  collapsible?: boolean;
  defaultOpen?: boolean;
  /** Controlled `open`. If provided alongside `onOpenChange`, parent owns state. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  appendixLabel?: string;
  children: ReactNode;
  /** Optional accent color for the title bar. */
  accent?: 'navy' | 'gold';
}

export default function ReportSection({
  title,
  subtitle,
  icon,
  collapsible = false,
  defaultOpen = true,
  open: openProp,
  onOpenChange,
  appendixLabel,
  children,
  accent = 'navy',
}: ReportSectionProps) {
  const isControlled = openProp !== undefined;
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const open = isControlled ? openProp : internalOpen;
  const expanded = collapsible ? open : true;

  const toggle = () => {
    const next = !open;
    if (isControlled) {
      onOpenChange?.(next);
    } else {
      setInternalOpen(next);
    }
  };

  const accentText = accent === 'gold' ? 'text-gold-700' : 'text-navy';

  return (
    <section className="card overflow-hidden report-section">
      <header
        className={[
          'flex items-start gap-3 p-4 md:p-5',
          collapsible ? 'cursor-pointer select-none collapsible-toggle-host' : '',
          collapsible ? 'hover:bg-navy-50/40' : '',
        ].join(' ')}
        onClick={collapsible ? toggle : undefined}
        aria-expanded={collapsible ? expanded : undefined}
        role={collapsible ? 'button' : undefined}
        tabIndex={collapsible ? 0 : undefined}
        onKeyDown={
          collapsible
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggle();
                }
              }
            : undefined
        }
      >
        {icon && (
          <span className={`flex-shrink-0 ${accentText}`} aria-hidden="true">
            {icon}
          </span>
        )}
        <div className="flex-1 min-w-0">
          {appendixLabel && (
            <span className="inline-block text-[10px] font-semibold uppercase tracking-wide text-gold-700 bg-gold-50 px-2 py-0.5 rounded mb-1">
              {appendixLabel}
            </span>
          )}
          <h2 className={`text-lg md:text-xl font-bold ${accentText}`}>{title}</h2>
          {subtitle && (
            <p className="text-sm text-navy-500 mt-0.5">{subtitle}</p>
          )}
        </div>
        {collapsible && (
          <span className="flex-shrink-0 text-navy-400 collapsible-toggle">
            {open ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </span>
        )}
      </header>

      {expanded && (
        <div className="px-4 md:px-5 pb-5 animate-fade-in">{children}</div>
      )}
    </section>
  );
}
